import * as React from 'react';
import { cn } from '@/lib/utils';

export interface CDCheckboxProps {
  /** Whether the checkbox is checked */
  checked?: boolean;
  /** Label text to display next to the checkbox */
  label?: string;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Whether the checkbox is disabled */
  disabled?: boolean;
  /** onChange handler for interactive mode */
  onChange?: (checked: boolean) => void;
  /** Additional className for the container */
  className?: string;
}

/**
 * CDCheckbox - CFPB-style checkbox with X mark
 *
 * Displays a 12x12px box with an X when checked.
 * Used for YES/NO indicators and selection fields.
 *
 * Styling:
 * - 12px x 12px square
 * - 1px black border
 * - White background
 * - Bold "X" when checked
 */
export const CDCheckbox = React.forwardRef<HTMLDivElement, CDCheckboxProps>(
  ({ checked = false, label, size = 'md', disabled = false, onChange, className }, ref) => {
    const handleClick = () => {
      if (!disabled && onChange) {
        onChange(!checked);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled && onChange) {
        e.preventDefault();
        onChange(!checked);
      }
    };

    const sizeClasses =
      size === 'sm' ? 'w-[10px] h-[10px] min-w-[10px] min-h-[10px] text-[8px]' : '';

    return (
      <div ref={ref} className={cn('inline-flex items-center gap-1', className)}>
        <div
          role="checkbox"
          aria-checked={checked}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          className={cn(
            'cd-checkbox',
            sizeClasses,
            checked && 'cd-checkbox-checked',
            disabled && 'opacity-50 cursor-not-allowed',
            !disabled && onChange && 'cursor-pointer hover:bg-gray-50',
          )}
        />
        {label && (
          <span className={cn(size === 'sm' ? 'cd-text-tiny' : 'cd-text-body')}>{label}</span>
        )}
      </div>
    );
  },
);

CDCheckbox.displayName = 'CDCheckbox';

/**
 * CDYesNo - YES/NO indicator pair
 *
 * Common pattern in CD forms showing two checkboxes for YES and NO options.
 */
export interface CDYesNoProps {
  /** Current value: true = YES, false = NO, undefined = neither */
  value?: boolean;
  /** onChange handler */
  onChange?: (value: boolean) => void;
  /** Whether the control is disabled */
  disabled?: boolean;
  /** Additional detail text to show after the selected option */
  detail?: string;
  /** Additional className */
  className?: string;
}

export const CDYesNo = React.forwardRef<HTMLDivElement, CDYesNoProps>(
  ({ value, onChange, disabled = false, detail, className }, ref) => {
    return (
      <div ref={ref} className={cn('inline-flex items-center gap-4', className)}>
        <CDCheckbox
          checked={value === true}
          label="YES"
          disabled={disabled}
          onChange={() => onChange?.(true)}
        />
        <CDCheckbox
          checked={value === false}
          label="NO"
          disabled={disabled}
          onChange={() => onChange?.(false)}
        />
        {detail && value !== undefined && <span className="cd-yes-no-detail">{detail}</span>}
      </div>
    );
  },
);

CDYesNo.displayName = 'CDYesNo';
